<script setup lang="ts">
import { Separator } from "@/components/ui/separator"

import MenuSheet from "./components/MenuSheet.vue"

import type { MenuItem } from "@/globals"

import { cn, getElementContent } from "@/lib/utils"
import { onMounted, provide, ref, watch } from "vue"
import { useRoute } from "vue-router"
import UserMenu from "./components/UserMenu.vue"
import currentUser, { getPermissions } from "@/lib/user"
import { Search } from "lucide-vue-next"
import { ClipLoader } from "vue3-spinner"

import LocaleSelect from "@/components/ui-custom/locale-select/LocaleSelect.vue"
import { Toaster } from "@/components/ui-custom/toast"

const imageSrc: string = "/logo.svg"
const menuItems: MenuItem[] = appModules

const route = useRoute()
const errorMessage = ref('')
const noticeMessage = ref('')

const timer = 5000

onMounted(() => {
  errorMessage.value = getElementContent('flash-error')
  noticeMessage.value = getElementContent('flash-notice')
})

watch(errorMessage, () => {
  setTimeout(() => {
    errorMessage.value = ''
  }, timer)
})

watch(noticeMessage, () => {
 setTimeout(() => {
   noticeMessage.value = ''
 }, timer)
})

provide("currentUser", currentUser)
provide("getPermissions", getPermissions)

const isActive = (item: any): boolean => {
  return item.path === route.path
}
</script>

<template>
  <div class="flex items-center flex-1 flex-col min-h-screen justify-center" v-if="currentUser.loading">
    <clip-loader />
  </div>

  <template v-else>
    <transition
      enter-active-class="transition-opacity duration-1000"
      leave-active-class="transition-opacity duration-1000"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="noticeMessage">
  <!--      <div class="absolute bg-red-400 py-3 text-white text-center w-full" v-if="errorMessage">{{ errorMessage }}</div>-->
        <div class="absolute bg-green-300 py-3 text-center w-full" v-if="noticeMessage">{{ noticeMessage }}</div>
      </div>
    </transition>

    <div class="flex min-h-screen flex-col">
      <div class="logo-header hidden bg-primary p-4 lg:flex">
        <div class="container items-center justify-center gap-8 lg:flex">
          <a href="https://santacecilia.it/">
            <img :src="imageSrc" alt="Logo" class="h-28 w-28 invert" />
          </a>

          <Separator class="h-24 bg-white" orientation="vertical" />

          <div class="flex flex-col text-white">
            <div class="text-2xl">SANTACECILIA.IT</div>
            <div class="text-4xl">STUDI MUSICALI ONLINE</div>
          </div>
        </div>
      </div>

      <header class="nav-header sticky top-0 z-50 mt-0 bg-primary px-2 shadow lg:border-t-4">
        <div class="container mx-auto flex items-center justify-between gap-4 py-1">
          <menu-sheet :items="menuItems" :image-src="imageSrc" />

          <div class="flex items-center lg:hidden">
            <a href="/">
              <img :src="imageSrc" alt="Logo" class="h-8 w-8 invert" />
            </a>

            <h1 class="ml-2 font-bold text-white">Studi Musicali Online</h1>
          </div>

          <nav class="hidden space-x-4 lg:flex">
            <router-link
              v-for="item of menuItems"
              :to="item.path"
              :class="cn(['font-medium text-muted hover:text-white', isActive(item) ? 'underline' : ''])">
              {{ $t(`commons.nav.${item.name}`) }}
            </router-link>
          </nav>

          <div class="ml-auto flex items-center gap-4">
            <router-link
              to="/advanced_search"
              :class="
                cn([
                  'hidden gap-1 font-medium text-muted hover:text-white lg:flex',
                  isActive({ path: '/advanced_search' }) ? 'underline' : ''
                ])
              ">
              <Search /> {{ $t("commons.nav.advanced_search") }}
            </router-link>

            <locale-select />

            <user-menu />
          </div>
        </div>
      </header>

      <main class="flex-1 px-0">
        <router-view />
      </main>

      <footer class="flex flex-col items-center justify-center gap-8 bg-primary p-4 lg:flex-row">
        <div class="container flex flex-col gap-1 font-semibold text-sm text-muted">
          <p>ACCADEMIA NAZIONALE DI SANTA CECILIA, AUDITORIUM PARCO DELLA MUSICA VIALE DE COUBERTIN, 00196 ROMA.</p>

          <p>T +39 06 80242501. PARTITA IVA 05662271005</p>

          <div class="flex flex-row py-2">
            <p class="text-gray-400">&copy; 2024 Accademia Nazionale di Santa Cecilia.</p>

            <nav class="ml-auto space-x-4 text-white">
              <router-link to="/privacy_policy" class="text-gray-500 hover:text-white">Privacy Policy</router-link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  </template>

  <Toaster />

</template>
